import '../index.css';
import React, { useEffect, useMemo, useState } from 'react'
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { FormControl, Select } from '@mui/material';
import { customStyle } from '@custom/Utils/utils';
import { AppDispatch, RootState } from '@custom/store/store';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { servicesManager } from '../../../app/src/App';
import getPrice from '@custom/rtk-apis/price/getPrice';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import { getIcon } from '@ohif/ui';

const DoctorDiscountForm = ({ editDoctor, initialValues, submitForm, edit, setEdit, setShowDiscountForm, isSubmitting }) => {

  const { uiNotificationService } = servicesManager.services;

  const dispatch = useDispatch<AppDispatch>();
  const modalityStudyResponse = useSelector((state: RootState) => state?.getModalityStudySliceResponse);
  const { result: pricingData, loading, error } = useSelector((state: RootState) => state.getPriceSliceResponse);

  const [modalityList, setModality] = useState([]);
  const [studyRegionList, setStudyRegion] = useState([]);
  const [disableDefaultPriceMap, setDisableDefaultPriceMap] = useState<{ [index: number]: boolean }>({});
  const memoizedInitialValues = useMemo(() => initialValues, [JSON.stringify(initialValues)]);

  const doctorDiscountSchema = Yup.object().shape({
    doctorName: Yup.string().required('Doctor Name is required'),
    investigationDiscount: Yup.mixed().test(
      'is-number-dash-or-percent',
      'Investigation Discount must be a number, a percentage (like "12%")',
      value =>
        value === "--" ||
        (!isNaN(Number(value?.toString())) && value?.toString() !== "") ||
        (/^\d+(\.\d+)?%$/.test(value?.toString()))
    ),
    investigationHikePrice: Yup.mixed().test(
      'is-number-dash-or-percent',
      'Investigation Hike Price must be a number, a percentage (like "12%")',
      value =>
        value === "--" ||
        (!isNaN(Number(value)) && value !== "") ||
        (/^\d+(\.\d+)?%$/.test(value?.toString()))
    ),
    priceFields: Yup.array().of(
      Yup.object().shape({
        modality: Yup.string(),
        studyRegion: Yup.string(),
        defaultPrice: Yup.string().when(['modality', 'studyRegion'], {
          is: (modality: string, studyRegion: string) => Boolean(modality && studyRegion),
          then: (schema) =>
            schema.required(`Default Price is required ${edit ? "Add value in pricing tab" : ""}`),
          otherwise: (schema) => schema.notRequired()
        }),
        priceDiscount: Yup.string(),
        priceHike: Yup.string()
      })
    )
  });

  const getSelectedCombinationsExcludingCurrent = (fields: any[], currentIndex: number) => {
    return fields
      .filter((f, i) => i !== currentIndex && f.modality && f.studyRegion)
      .map(f => `${f.modality}__${f.studyRegion}`);
  };

  useEffect(() => {
    dispatch(getModalityStudy());
    setDisableDefaultPriceMap(initialValues.priceFields.reduce((acc, field, index) => {
      acc[index] = field.modality && field.studyRegion ? true : false;
      return acc;
    }, {}));
  }, []);

  useEffect(() => {
    if (modalityStudyResponse?.status === 'SUCCESS') {
      setModality(modalityStudyResponse?.result?.modalities);
      setStudyRegion(modalityStudyResponse?.result?.studyRegions);
    }
  }, [modalityStudyResponse?.status]);

  return (
    <div className="details">
      <div className="form form--innersec">
        <div className="mb-3">
          <button
            className=''
            onClick={() => setShowDiscountForm(false)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {getIcon('icon-toolbar-back')}
            Back
          </button>
        </div>
        <section className="form__section">
          <h1 className="form__section__title">
            {editDoctor ? 'Edit Doctor Discount/Hike' : 'Add Doctor Discount/Hike'}
          </h1>
          <Formik
            initialValues={memoizedInitialValues}
            validationSchema={doctorDiscountSchema}
            onSubmit={(values, { setSubmitting, setErrors }) => {
              console.log("Submitting", values);
              values.investigationDiscount = values.investigationDiscount.replace('%', '');
              values.investigationHikePrice = values.investigationHikePrice.replace('%', '');
              // optionally validate again
              // doctorDiscountSchema.validate(values, { abortEarly: false }).catch(err => {
              //   console.log("Validation errors:", err.inner);
              // });
              // your submit logic here
              submitForm(values);
            }}
            enableReinitialize={true}>
            {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
            {
              return(
              <Form onSubmit={handleSubmit}>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label" htmlFor="doctorName">
                        Doctor Name<span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        className="form__input form__input--sm mt-1"
                        id="doctorName"
                        name="doctorName"
                        placeholder="Name"
                        value={values.doctorName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                        <ErrorMessage name='doctorName' component='span' className='text-red-500 text-sm font-medium' />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label" htmlFor="investigationDiscount">
                        Investigation Discount %
                      </label>
                      <input
                        type="text"
                        className={`form__input form__input--sm mt-1 ${(values.investigationHikePrice && values.investigationHikePrice !== "--") ? 'disabled-input' : ''}`}
                        id="investigationDiscount"
                        name="investigationDiscount"
                        placeholder="Discount(%)"
                        value={values.investigationDiscount ? `${values.investigationDiscount}` : ""}
                        onChange={(e) => {
                          let value = e.target.value.replace('%', '');
                          if (value === "") {
                            setFieldValue("investigationDiscount", "");
                          } else if (/^\d*$/.test(value)) {
                            let num = Math.min(Number(value), 100);
                            setFieldValue("investigationDiscount", num.toString());
                            if (value) setFieldValue("investigationHikePrice", "--");
                          }
                        }}
                        onBlur={handleBlur}
                        autoComplete="off"
                        disabled={!!(values.investigationHikePrice && values.investigationHikePrice !== "--")}
                      />
                        <ErrorMessage name='investigationDiscount' component='span' className='text-red-500 text-sm font-medium' />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label" htmlFor="investigationHikePrice">
                        Investigation Price Hike %
                      </label>
                      <input
                        type="text"
                        className={`form__input form__input--sm mt-1 ${(values.investigationDiscount && values.investigationDiscount !== "--") ? 'disabled-input' : ''}`}
                        id="investigationHikePrice"
                        name="investigationHikePrice"
                        placeholder="Hike Price(%)"
                        value={
                          values.investigationHikePrice === "--" || values.investigationHikePrice === ""
                            ? ""
                            : `${values.investigationHikePrice}`
                        }
                        onChange={(e) => {
                          let value = e.target.value.replace('%', '');
                          if (value === "") {
                            setFieldValue("investigationHikePrice", "");
                          } else if (/^\d*(\.\d{0,2})?$/.test(value)) {
                            let num = Math.min(Number(value), 100);
                            setFieldValue("investigationHikePrice", num.toString());
                            if (value) setFieldValue("investigationDiscount", "--");
                          }
                        }}
                        onBlur={handleBlur}
                        autoComplete="off"
                        disabled={!!(values.investigationDiscount && values.investigationDiscount !== "--")}
                      />
                        <ErrorMessage name='investigationHikePrice' component='span' className='text-red-500 text-sm font-medium' />
                    </div>
                  </div>
                </div>

                <FieldArray name="priceFields">
                  {({ push, remove }) => (
                    <div>
                      {values.priceFields.map((field, index) => (
                        <div key={index} className="price-field-group">
                          <div className="formRow formRow__2">
                            <div className='formCol'>
                              <div className='formRow formRow__2'>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">
                                      Modality
                                      {index === 0 && <button
                                        type="button"
                                        className="text-lg text-[#0F8491] ml-2"
                                        onClick={() => push({
                                          modality: '',
                                          studyRegion: '',
                                          defaultPrice: '',
                                          priceDiscount: '',
                                          priceHike: ''
                                        })}
                                      >
                                        + Add More
                                      </button>}
                                      {index > 0 && (
                                        <button
                                          type="button"
                                          className="text-lg text-red-600 ml-2"
                                          onClick={() => remove(index)}
                                        >
                                          - Remove
                                        </button>
                                      )}
                                    </label>
                                    <select
                                      className="form__select form__select--sm"
                                      name={`priceFields.${index}.modality`}
                                      value={field.modality}
                                      style={customStyle.select}
                                      onChange={(e) => {
                                        handleChange(e);
                                        if(field.studyRegion) {
                                          dispatch(getPrice({modality: e.target.value, studyRegion: field.studyRegion})).unwrap().then((res) => {
                                            if (res.status === 'SUCCESS') {
                                              if(res?.result?.length === 0) {
                                                setFieldValue(`priceFields.${index}.defaultPrice`, '');
                                                uiNotificationService.show({
                                                  title: 'Pricing',
                                                  message: "Price not found, please add price first",
                                                  type: 'error',
                                                });
                                                setDisableDefaultPriceMap(prev => ({ ...prev, [index]: false }));
                                                return;
                                              } else {
                                                setDisableDefaultPriceMap(prev => ({ ...prev, [index]: true }));
                                                setFieldValue(`priceFields.${index}.defaultPrice`, res?.result?.[0]?.price);
                                              }
                                            } else {
                                              setDisableDefaultPriceMap(prev => ({ ...prev, [index]: false }));
                                              setFieldValue(`priceFields.${index}.defaultPrice`, '');
                                            }
                                          })
                                        }
                                      }}
                                      onBlur={handleBlur}
                                    >
                                      <option value="">-Select-</option>
                                      {modalityList
                                        .filter((modality) => {
                                          const selectedCombos = getSelectedCombinationsExcludingCurrent(values.priceFields, index);
                                          const comboKey = `${modality}__${field.studyRegion}`;
                                          return !selectedCombos.includes(comboKey) || modality === field.modality;
                                        })
                                        .map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">
                                      Study Region
                                    </label>
                                    <select
                                      className="form__select form__select--sm"
                                      name={`priceFields.${index}.studyRegion`}
                                      value={field.studyRegion}
                                      style={customStyle.select}
                                      onChange={(e) => {
                                        handleChange(e);
                                        if (field.modality) {
                                          dispatch(getPrice({modality: field.modality, studyRegion: e.target.value})).unwrap().then((res) => {
                                            if (res.status === 'SUCCESS') {
                                              if(res?.result?.length === 0) {
                                                setFieldValue(`priceFields.${index}.defaultPrice`, '');
                                                uiNotificationService.show({
                                                  title: 'Pricing',
                                                  message: "Price not found, please add price first",
                                                  type: 'error',
                                                });
                                                setDisableDefaultPriceMap(prev => ({ ...prev, [index]: false }));
                                                return;
                                              } else {
                                                setDisableDefaultPriceMap(prev => ({ ...prev, [index]: true }));
                                                setFieldValue(`priceFields.${index}.defaultPrice`, res?.result?.[0]?.price);
                                              }
                                            } else {
                                              setDisableDefaultPriceMap(prev => ({ ...prev, [index]: false }));
                                              setFieldValue(`priceFields.${index}.defaultPrice`, '');
                                            }
                                          })
                                        }
                                      }}
                                      onBlur={handleBlur}
                                    >
                                      <option value="">-Select-</option>
                                      {studyRegionList
                                        .filter((region) => {
                                          const selectedCombos = getSelectedCombinationsExcludingCurrent(values.priceFields, index);
                                          const comboKey = `${field.modality}__${region}`;
                                          return !selectedCombos.includes(comboKey) || region === field.studyRegion;
                                        })
                                        .map((option) => (
                                          <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='formCol'>
                              <div className="formRow formRow__3">
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">Default Price</label>
                                    <input
                                      type="text"
                                      className={`form__input form__input--sm
                                        ${disableDefaultPriceMap[index] ? 'disabled-input' : ''}
                                      ${
                                        values.priceFields[index].modality &&
                                        values.priceFields[index].studyRegion &&
                                        !values.priceFields[index].defaultPrice ? 'error-input' : ''
                                      }`}
                                      name={`priceFields.${index}.defaultPrice`}
                                      value={values.priceFields[index].defaultPrice}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder={'Default Price'}
                                      disabled={disableDefaultPriceMap[index]}
                                    />
                                    {values.priceFields[index].modality &&
                                    values.priceFields[index].studyRegion &&
                                    !values.priceFields[index].defaultPrice && (
                                      <ErrorMessage name={`priceFields.${index}.defaultPrice`} component='span' className='text-red-500 text-sm font-medium' />
                                    )}
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">Price Discount %</label>
                                    <input
                                      type="text"
                                      className={`form__input form__input--sm ${field.priceHike ? 'disabled-input' : ''}`}
                                      name={`priceFields.${index}.priceDiscount`}
                                      value={
                                        field.priceDiscount === ""
                                          ? field.priceDiscount
                                          : `${field.priceDiscount}%`
                                      }
                                      placeholder={'Price Discount(%)'}
                                      onChange={(e) => {
                                        let value = e.target.value.replace('%', '');
                                        if (value === "") {
                                          setFieldValue(`priceFields.${index}.priceDiscount`, "");
                                        } else if (/^\d*$/.test(value)) {
                                          const num = Math.min(Number(value), 100);
                                          setFieldValue(`priceFields.${index}.priceDiscount`, num.toString());
                                          if (value) setFieldValue(`priceFields.${index}.priceHike`, "");
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      disabled={!!field.priceHike}
                                    />
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">Price Hike %</label>
                                    <input
                                      type="text"
                                      className={`form__input form__input--sm ${field.priceDiscount ? 'disabled-input' : ''}`}
                                      name={`priceFields.${index}.priceHike`}
                                      value={
                                        field.priceHike === ""
                                          ? field.priceHike
                                          : `${field.priceHike}%`
                                      }
                                      placeholder={'Price Hike(%)'}
                                      onChange={(e) => {
                                        let value = e.target.value.replace('%', '');
                                        if (value === "") {
                                          setFieldValue(`priceFields.${index}.priceHike`, "");
                                        } else if (/^\d*(\.\d{0,2})?$/.test(value)) {
                                          const num = Math.min(Number(value), 100);
                                          setFieldValue(`priceFields.${index}.priceHike`, num.toString());
                                          if (value) setFieldValue(`priceFields.${index}.priceDiscount`, "");
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      disabled={!!field.priceDiscount}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FieldArray>

                <div className="btn__group">
                  <button
                    className="btn btn__white"
                    type="button"
                    onClick={() => {
                      setShowDiscountForm(false);
                      setEdit(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button className="btn btn__primary" type="submit" disabled={isSubmitting}>
                    {edit ? 'Update' : 'Save'}
                  </button>
                </div>
              </Form>
            )}}}
          </Formik>
        </section>
      </div>
    </div>
  );
};

export default DoctorDiscountForm
